import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2, Link as LinkIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUpload — permite tanto upload de arquivo local quanto colar URL externa.
 * Valor final é sempre uma string (URL) armazenada no campo do form.
 * Preview mostra o arquivo carregado. Botão trocar/remover.
 */
export default function ImageUpload({ value, onChange, testId, aspect = "aspect-[3/2]", allowUrl = true }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState("");

  const resolvedUrl = (() => {
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) return value;
    if (value.startsWith("/api/")) return `${BACKEND_URL}${value}`;
    return value;
  })();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagem carregada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    e.target.value = ""; // allow re-selecting the same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const applyUrl = () => {
    if (urlValue.trim()) {
      onChange(urlValue.trim());
      setUrlValue("");
      setShowUrlInput(false);
    }
  };

  return (
    <div data-testid={testId ? `${testId}-wrapper` : undefined}>
      {resolvedUrl ? (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className={`${aspect} w-40 shrink-0 overflow-hidden bg-black/30 border border-white/10 rounded`}>
            <img src={resolvedUrl} alt="preview" className="w-full h-full object-cover" onError={(e) => (e.target.style.opacity = 0.2)} />
          </div>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="text-xs text-white/60 truncate" title={value}>{value}</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={testId ? `${testId}-replace` : undefined}
                className="text-xs px-3 py-1.5 border border-[color:var(--marupiara-gold)]/40 text-[color:var(--marupiara-gold)] hover:bg-[color:var(--marupiara-gold)]/10 flex items-center gap-1 disabled:opacity-50">
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Trocar
              </button>
              {allowUrl && (
                <button type="button" onClick={() => { setShowUrlInput(!showUrlInput); setUrlValue(value || ""); }}
                  className="text-xs px-3 py-1.5 border border-white/15 text-white/70 hover:text-white flex items-center gap-1">
                  <LinkIcon size={12} /> URL
                </button>
              )}
              <button type="button" onClick={() => onChange("")} data-testid={testId ? `${testId}-clear` : undefined}
                className="text-xs px-3 py-1.5 border border-red-400/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1">
                <X size={12} /> Remover
              </button>
            </div>
            {showUrlInput && (
              <div className="flex gap-2">
                <input value={urlValue} onChange={(e) => setUrlValue(e.target.value)} placeholder="Cole a URL da imagem…"
                  className="flex-1 bg-black/40 border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[color:var(--marupiara-gold)] focus:outline-none" />
                <button type="button" onClick={applyUrl} className="text-xs px-3 py-1.5 bg-[color:var(--marupiara-gold)] text-[color:var(--marupiara-obsidian)]">OK</button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div
            onClick={() => inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid={testId ? `${testId}-dropzone` : undefined}
            className="border border-dashed border-[color:var(--marupiara-gold)]/40 hover:border-[color:var(--marupiara-gold)] bg-black/20 hover:bg-black/30 transition-colors cursor-pointer py-8 px-4 flex flex-col items-center gap-2 text-center"
          >
            {uploading ? (
              <><Loader2 size={22} className="animate-spin text-[color:var(--marupiara-gold)]" /><span className="text-sm text-white/70">Enviando…</span></>
            ) : (
              <>
                <ImageIcon size={22} className="text-[color:var(--marupiara-gold)]" />
                <span className="text-sm text-white"><span className="font-medium">Clique para enviar</span> ou arraste uma imagem aqui</span>
                <span className="text-[11px] text-white/50">PNG, JPG, WEBP, GIF, AVIF • máx 8 MB</span>
              </>
            )}
          </div>
          {allowUrl && (
            <div className="mt-2 flex justify-end">
              <button type="button" onClick={() => setShowUrlInput(!showUrlInput)} className="text-[11px] text-white/60 hover:text-[color:var(--marupiara-gold)] flex items-center gap-1">
                <LinkIcon size={11} /> ou usar URL externa
              </button>
            </div>
          )}
          {showUrlInput && allowUrl && (
            <div className="mt-2 flex gap-2">
              <input value={urlValue} onChange={(e) => setUrlValue(e.target.value)} placeholder="https://…"
                className="flex-1 bg-black/40 border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[color:var(--marupiara-gold)] focus:outline-none" />
              <button type="button" onClick={applyUrl} className="text-xs px-3 py-1.5 bg-[color:var(--marupiara-gold)] text-[color:var(--marupiara-obsidian)]">OK</button>
            </div>
          )}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid={testId ? `${testId}-file-input` : undefined} />
    </div>
  );
}

/** Resolve a URL that may be either absolute or a relative /api/files path. */
export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}
